import type { CSSProperties, HTMLAttributes } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { colors } from '../../../../styles/colors';
import { radii, space } from '../../../../styles/onboardingUiTokens';

export type DualPhotoMarqueeImage = {
  src: string;
  srcSet?: string;
};

const CYCLE_LEN = 3;

const PLACEHOLDER_GRADIENTS = [
  `linear-gradient(135deg, color-mix(in srgb, ${colors.primary} 18%, ${colors.light}) 0%, ${colors.light} 100%)`,
  `linear-gradient(135deg, color-mix(in srgb, ${colors.medium} 14%, ${colors.light}) 0%, ${colors.light} 100%)`,
  `linear-gradient(135deg, color-mix(in srgb, ${colors.primary} 10%, ${colors.white}) 0%, ${colors.light} 100%)`,
] as const;

export type DualPhotoMarqueeProps = {
  /** Image strip for the left column (scrolls downward when animated). First three are used. */
  leftImages: DualPhotoMarqueeImage[];
  /** Image strip for the right column (scrolls upward when animated). First three are used. */
  rightImages: DualPhotoMarqueeImage[];
  /** Duration in seconds for one full loop over the three-image cycle. */
  durationSec?: number;
  /** Viewport height of the marquee. */
  height?: CSSProperties['height'];
  /** Install screen should use `eager` to avoid lazy-load issues inside animated overflow. */
  imageLoading?: 'lazy' | 'eager';
  /** Fired when a tile image fails to load (after course URLs are shown). */
  onImageError?: () => void;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>;

type Layout = {
  tile: number;
  gap: number;
};

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

/** Oversized shell so edge fades sit above animated tiles (avoids compositor flash on some browsers). */
const FadeWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  width: calc(100% + 10px);
  margin: -5px;
  padding: 5px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    z-index: 2;
    pointer-events: none;
    height: clamp(3.25rem, 38%, 8rem);
  }

  &::before {
    top: 0;
    background: linear-gradient(
      to bottom,
      ${colors.white} 0%,
      rgba(255, 255, 255, 0.72) 38%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &::after {
    bottom: 0;
    background: linear-gradient(to top, ${colors.white} 0%, rgba(255, 255, 255, 0.72) 38%, rgba(255, 255, 255, 0) 100%);
  }
`;

const Root = styled.div<{ $height: NonNullable<DualPhotoMarqueeProps['height']> }>`
  box-sizing: border-box;
  position: relative;
  z-index: 0;
  width: 100%;
  height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height)};
  display: flex;
  flex-direction: row;
  gap: ${space.sm};
  min-width: 0;
  overflow: hidden;
`;

const ColumnMask = styled.div`
  position: relative;
  z-index: 0;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  transform: rotate(var(--dm-tilt));
`;

/** Same slight clockwise tilt for both columns; marquee keyframes stay translate-only. */
const TiltStrip = styled.div`
  width: 100%;
  height: 100%;
  transform-origin: 50% 0;
`;

/** One full loop: half-tile start → one cycle height further (same phase after one period). */
const marqueeShift = keyframes`
  from {
    transform: translate3d(0, calc(-0.5 * var(--dm-tile)), 0);
  }
  to {
    transform: translate3d(0, calc(-0.5 * var(--dm-tile) - var(--dm-cycle)), 0);
  }
`;

/** Right column: same motion path plus vertical stagger between columns. */
const marqueeShiftStaggered = keyframes`
  from {
    transform: translate3d(0, calc(-0.5 * var(--dm-tile) + var(--dm-stagger)), 0);
  }
  to {
    transform: translate3d(0, calc(-0.5 * var(--dm-tile) + var(--dm-stagger) - var(--dm-cycle)), 0);
  }
`;

const Track = styled.div<{
  $durationSec: number;
  $reverse: boolean;
  $animated: boolean;
  $staggered: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--dm-gap);
  width: 100%;
  will-change: transform;

  ${({ $animated, $durationSec, $reverse, $staggered }) =>
    $animated
      ? css`
          animation: ${$staggered ? marqueeShiftStaggered : marqueeShift} ${$durationSec}s linear infinite;
          animation-direction: ${$reverse ? 'reverse' : 'normal'};
        `
      : css`
          animation: none;
          transform: translate3d(
            0,
            ${$staggered ? 'calc(-0.5 * var(--dm-tile) + var(--dm-stagger))' : 'calc(-0.5 * var(--dm-tile))'},
            0
          );
        `}
`;

const Tile = styled.div<{ $gradient: string }>`
  position: relative;
  width: 100%;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
  border-radius: ${radii.lg};
  overflow: hidden;
  background: ${({ $gradient }) => $gradient};
`;

const TileSkeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, ${colors.white} 55%, transparent) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const TileImg = styled.img<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.28s ease-out;
`;

function subscribeReducedMotion(callback: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined;
  }
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function getReducedMotionSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getReducedMotionServerSnapshot(): boolean {
  return false;
}

function pickThree(
  images: DualPhotoMarqueeImage[],
): [DualPhotoMarqueeImage, DualPhotoMarqueeImage, DualPhotoMarqueeImage] {
  const empty: DualPhotoMarqueeImage = { src: '' };
  if (images.length === 0) {
    return [empty, empty, empty];
  }
  const pad = images[0] ?? empty;
  const a = images[0] ?? pad;
  const b = images[1] ?? a;
  const c = images[2] ?? b;
  return [a, b, c];
}

function parseGapPx(rootEl: HTMLElement | null): number {
  if (!rootEl) {
    return 8;
  }
  const cs = getComputedStyle(rootEl);
  const raw = cs.columnGap || cs.gap || cs.rowGap || '8px';
  const first = raw.split(/\s+/)[0] ?? '8px';
  const px = parseFloat(first);
  return Number.isFinite(px) ? px : 8;
}

function MarqueeImageTile({
  image,
  tileIndex,
  imageLoading,
  onImageError,
}: {
  image: DualPhotoMarqueeImage;
  tileIndex: number;
  imageLoading: 'lazy' | 'eager';
  onImageError?: () => void;
}) {
  const src = image.src.trim();
  const hasSrc = src.length > 0;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
      setFailed(false);
      return;
    }
    const id = window.setTimeout(() => {
      setLoaded(false);
      setFailed(false);
    }, 0);
    return () => window.clearTimeout(id);
  }, [src]);

  const gradient = PLACEHOLDER_GRADIENTS[tileIndex % PLACEHOLDER_GRADIENTS.length] ?? PLACEHOLDER_GRADIENTS[0];
  const showSkeleton = hasSrc && !loaded && !failed;

  return (
    <Tile $gradient={gradient}>
      {showSkeleton ? <TileSkeleton aria-hidden /> : null}
      {hasSrc ? (
        <TileImg
          ref={imgRef}
          src={src}
          srcSet={image.srcSet}
          alt=""
          loading={imageLoading}
          decoding="async"
          draggable={false}
          fetchPriority={imageLoading === 'eager' ? 'high' : undefined}
          $loaded={loaded && !failed}
          onLoad={() => {
            setFailed(false);
            setLoaded(true);
          }}
          onError={() => {
            setFailed(true);
            setLoaded(false);
            onImageError?.();
          }}
        />
      ) : null}
    </Tile>
  );
}

function buildTiles(
  images: [DualPhotoMarqueeImage, DualPhotoMarqueeImage, DualPhotoMarqueeImage],
  keyPrefix: string,
  imageLoading: 'lazy' | 'eager',
  onImageError?: () => void,
) {
  return images.map((img, index) => (
    <MarqueeImageTile
      key={`${keyPrefix}-${index}-${img.src}`}
      image={img}
      tileIndex={index}
      imageLoading={imageLoading}
      onImageError={onImageError}
    />
  ));
}

/**
 * Two side-by-side columns of square photos in a fixed-height viewport. Each column repeats
 * three images on a duplicated strip animated with **CSS keyframes + transform** (composited,
 * no per-frame React updates). Starts with half a tile cropped at the top; left scrolls down,
 * right scrolls up with a stagger. Both columns share a slight clockwise tilt (parallel);
 * column gap matches vertical tile gap. A fade wrapper draws tall soft white vertical fades at
 * top and bottom so images appear to dissolve into the page. Honors `prefers-reduced-motion`.
 * Empty `src` values render gradient placeholders; non-empty sources show a skeleton until loaded.
 */
export function DualPhotoMarquee({
  leftImages,
  rightImages,
  durationSec = 48,
  height = 'clamp(16rem, 48vh, 28rem)',
  imageLoading = 'lazy',
  onImageError,
  className,
  style,
  ...rest
}: DualPhotoMarqueeProps) {
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );

  const rootRef = useRef<HTMLDivElement | null>(null);
  const measureColRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);

  const measure = useCallback(() => {
    const el = measureColRef.current;
    if (!el) {
      return;
    }
    const tile = el.clientWidth;
    if (tile <= 0) {
      return;
    }
    const gap = parseGapPx(rootRef.current);
    setLayout((prev) => {
      if (prev && prev.tile === tile && prev.gap === gap) {
        return prev;
      }
      return { tile, gap };
    });
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const el = measureColRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, layout]);

  const leftTriple = useMemo(() => pickThree(leftImages), [leftImages]);
  const rightTriple = useMemo(() => pickThree(rightImages), [rightImages]);

  const cssVars = useMemo((): CSSProperties | undefined => {
    if (!layout) {
      return undefined;
    }
    const { tile, gap } = layout;
    const cycle = CYCLE_LEN * tile + (CYCLE_LEN - 1) * gap;
    const stagger = (tile + gap) / 2;
    return {
      ['--dm-tile' as string]: `${tile}px`,
      ['--dm-gap' as string]: `${gap}px`,
      ['--dm-cycle' as string]: `${cycle}px`,
      ['--dm-stagger' as string]: `${stagger}px`,
      ['--dm-tilt' as string]: '3deg',
      gap: `${gap}px`,
    };
  }, [layout]);

  const leftTrack = useMemo(
    () => [
      ...buildTiles(leftTriple, 'L0', imageLoading, onImageError),
      ...buildTiles(leftTriple, 'L1', imageLoading, onImageError),
    ],
    [leftTriple, imageLoading, onImageError],
  );
  const rightTrack = useMemo(
    () => [
      ...buildTiles(rightTriple, 'R0', imageLoading, onImageError),
      ...buildTiles(rightTriple, 'R1', imageLoading, onImageError),
    ],
    [rightTriple, imageLoading, onImageError],
  );

  return (
    <FadeWrapper {...rest} className={className} style={style} aria-hidden>
      <Root ref={rootRef} $height={height} style={cssVars}>
        {!layout ? (
          <>
            <ColumnMask ref={measureColRef} />
            <ColumnMask />
          </>
        ) : (
          <>
            <ColumnMask ref={measureColRef}>
              <TiltStrip>
                <Track $durationSec={durationSec} $reverse $animated={!reducedMotion} $staggered={false}>
                  {leftTrack}
                </Track>
              </TiltStrip>
            </ColumnMask>
            <ColumnMask>
              <TiltStrip>
                <Track $durationSec={durationSec} $reverse={false} $animated={!reducedMotion} $staggered>
                  {rightTrack}
                </Track>
              </TiltStrip>
            </ColumnMask>
          </>
        )}
      </Root>
    </FadeWrapper>
  );
}
